import React, { useContext, useState, useEffect } from 'react';
import moment from 'moment';
import { Link } from 'react-router-dom';

// @material-ui/core components
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import TableBody from '@material-ui/core/TableBody';
import TableFooter from '@material-ui/core/TableFooter';
import ErrorIcon from '@material-ui/icons/Error';
import { makeStyles } from '@material-ui/core/styles';

import { ConsoleContext } from 'services/context';

import {
  formatMoney,
  formatNumber,
  getDefaultCurrency,
} from 'services/finance';

import { formatUserName } from 'services/user';

const useStyles = makeStyles((theme) => ({
  container: {
    position: 'relative',
    overflow: 'auto',
    maxWidth: '100%',
    '&::-webkit-scrollbar': {
      height: '8px',
    },
    '&::-webkit-scrollbar-track': {
      background: theme.palette.grey[100],
    },
    '&::-webkit-scrollbar-thumb': {
      background: theme.palette.grey[400],
      borderRadius: '4px',
    },
  },
  table: {
    minWidth: 650,
    borderCollapse: 'separate',
    borderSpacing: 0,
  },
  stickyColumn: {
    position: 'sticky',
    left: 0,
    background: theme.palette.background.paper,
    zIndex: 1,
    '&::after': {
      content: '""',
      position: 'absolute',
      right: 0,
      top: 0,
      bottom: 0,
      width: '4px',
      background: 'linear-gradient(90deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0) 100%)',
      pointerEvents: 'none',
    },
  },
  stickyColumnHeader: {
    position: 'sticky',
    left: 0,
    background: theme.palette.grey[50],
    zIndex: 2,
    '&::after': {
      content: '""',
      position: 'absolute',
      right: 0,
      top: 0,
      bottom: 0,
      width: '4px',
      background: 'linear-gradient(90deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0) 100%)',
      pointerEvents: 'none',
    },
  },
  stickyColumnFooter: {
    position: 'sticky',
    left: 0,
    background: theme.palette.grey[100],
    zIndex: 1,
    '&::after': {
      content: '""',
      position: 'absolute',
      right: 0,
      top: 0,
      bottom: 0,
      width: '4px',
      background: 'linear-gradient(90deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0) 100%)',
      pointerEvents: 'none',
    },
  },
  userName: {
    display: 'flex',
    alignItems: 'center',
    '& > *': {
      marginRight: theme.spacing(1),
    },
  },
  errorIcon: {
    color: theme.palette.error.main,
  },
  cell: {
    whiteSpace: 'nowrap',
    minWidth: '120px',
  },
  rateRow: {
    backgroundColor: theme.palette.grey[50],
  },
  totalRow: {
    backgroundColor: theme.palette.grey[100],
    fontWeight: 'bold',
  },
}));

export default function YearlyPayrollReport({ payrollReport }) {
  const { t } = useContext(ConsoleContext);
  const months = moment.months();
  const classes = useStyles();

  const [userRows, setUserRows] = useState([]);
  const [exchangeRates, setExchangeRates] = useState(Array(12).fill(null));
  const [monthlyTotals, setMonthlyTotals] = useState(Array(12).fill(0));

  const baseCurrency = getDefaultCurrency();

  const convertAmount = (amount, rates) => {
    if (!rates || !rates[baseCurrency]) return amount;
    return amount / rates[baseCurrency];
  };

  const calculateMonthlyTotals = (items) => {
    const totals = Array(12).fill(0);

    items.forEach((row) => {
      row.payments.forEach((amount, monthIndex) => {
        totals[monthIndex] += amount || 0;
      });
    });

    return totals;
  };

  const prepareReport = (payrollReport) => {
    if (!Array.isArray(payrollReport)) {
      console.warn('payrollReport is not an array:', payrollReport);
      return;
    }

    const itemsPerUser = {};
    const monthlyRates = Array(12).fill(null);

    payrollReport.forEach((payroll, index) => {
      if (!payroll?.records) {
        console.warn('Invalid payroll data at index', index, payroll);
        return;
      }

      const month = index;

      // Store exchange rates for each month
      if (payroll.currencyExchangeRates) {
        monthlyRates[month] = payroll.currencyExchangeRates;
      }

      payroll.records
        .filter((record) => record?.paymentFinalAmount > 0)
        .forEach((record) => {
          if (!itemsPerUser[record.userId]) {
            itemsPerUser[record.userId] = {
              userId: record.userId,
              userName: renderUserName(record),
              payments: Array(12).fill(0),
              total: 0,
            };
          }

          const item = itemsPerUser[record.userId];
          const convertedAmount = convertAmount(record.paymentFinalAmount, payroll.currencyExchangeRates);
          item.payments[month] = convertedAmount;
          item.total += convertedAmount;
        });
    });

    const items = Object.values(itemsPerUser);
    const totals = calculateMonthlyTotals(items);

    setUserRows(items);
    setExchangeRates(monthlyRates);
    setMonthlyTotals(totals);
  };

  const renderUserName = (record) => {
    const {
      _id,
      user,
      noContractDates,
    } = record;

    return (
      <div className={classes.userName}>
        <Link to={`payroll/${_id}`}>{formatUserName(user)}</Link>
        {noContractDates?.length > 0 && (
          <ErrorIcon className={classes.errorIcon} titleAccess={t('Missing Contracts')} />
        )}
      </div>
    );
  };

  useEffect(() => {
    prepareReport(payrollReport);
  }, [payrollReport]);

  if (!Array.isArray(userRows)) {
    return null;
  }

  const grandTotal = monthlyTotals.reduce((sum, amount) => sum + (amount || 0), 0);

  return (
    <div className={classes.container}>
      <Table className={classes.table}>
        <TableHead>
          <TableRow className={classes.rateRow}>
            <TableCell className={`${classes.cell} ${classes.stickyColumnHeader}`}>{t('Exchange Rates')}</TableCell>
            {exchangeRates.map((rates, index) => (
              <TableCell key={index} className={classes.cell}>
                {rates ? `1 ${baseCurrency} = ${formatNumber(rates[baseCurrency])} UAH` : '-'}
              </TableCell>
            ))}
            <TableCell className={classes.cell}>-</TableCell>
          </TableRow>
          <TableRow>
            <TableCell className={`${classes.cell} ${classes.stickyColumnHeader}`}>{t('Partner')}</TableCell>
            {months.map((month) => (
              <TableCell key={month} className={classes.cell}>{month}</TableCell>
            ))}
            <TableCell className={classes.cell}>{t('Total')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {userRows.map((row) => (
            <TableRow key={row.userId}>
              <TableCell className={`${classes.cell} ${classes.stickyColumn}`}>{row.userName}</TableCell>
              {row.payments.map((amount, index) => (
                <TableCell key={index} className={classes.cell}>{formatMoney(amount, false)}</TableCell>
              ))}
              <TableCell className={classes.cell}>{formatMoney(row.total, false)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
        <TableFooter>
          <TableRow className={classes.totalRow}>
            <TableCell className={`${classes.cell} ${classes.stickyColumnFooter}`}>{t('Month Total')}</TableCell>
            {monthlyTotals.map((total, index) => (
              <TableCell key={index} className={classes.cell}>{formatMoney(total, false)}</TableCell>
            ))}
            <TableCell className={classes.cell}>{formatMoney(grandTotal, false)}</TableCell>
          </TableRow>
        </TableFooter>
      </Table>
    </div>
  );
}
